import React, { useEffect, useState } from 'react';
import { Feature } from '@/types/feature.types';
import { useChimeSDK } from '@/hooks/useChimeSDK';
import { useCallTimer } from '@/hooks/useCallTimer';
import { ConnectionData } from '@/types/connect.types';
import { debug } from '@/config/constants';
import { Waveform } from './Waveform';
import { CallTimer } from './CallTimer';
import { CallControls } from './CallControls';
import { QueueDisplay } from './QueueDisplay';
import { LoadingOverlay } from '@/components/common';
import styles from './VoiceInterface.module.css';

interface VoiceInterfaceProps {
  isOpen: boolean;
  onClose: () => void;
  feature: Feature | null;
  connectionData: ConnectionData | null;
  sessionId: string | null;
  queueId: string | null; // For metrics tracking if call came from queue
  queueJoinTime: number | null; // Timestamp when user joined queue
  maxDuration: number; // in seconds
  isInQueue: boolean;
  queuePosition: number;
  queueLength: number;
  estimatedWaitTime: number;
  isQueueConnected: boolean;
  onCancelQueue: () => void;
}

export const VoiceInterface: React.FC<VoiceInterfaceProps> = ({
  isOpen,
  onClose,
  feature,
  connectionData,
  sessionId,
  queueId,
  queueJoinTime,
  maxDuration,
  isInQueue,
  queuePosition,
  queueLength,
  estimatedWaitTime,
  isQueueConnected,
  onCancelQueue,
}) => {
  const { initializeSession, endSession, toggleMute, isMuted, isConnected, error: chimeError } = useChimeSDK();
  const { elapsedTime, formattedTime, isWarning, start, stop, reset } = useCallTimer(maxDuration);
  const [isInitializing, setIsInitializing] = useState(false);
  const [wasConnected, setWasConnected] = useState(false);
  const [callStartTime, setCallStartTime] = useState<number>(0);

  // Track when we become connected
  useEffect(() => {
    if (isConnected) {
      setWasConnected(true);
    }
  }, [isConnected]);

  // Handle Chime errors - auto-close modal
  useEffect(() => {
    if (chimeError) {
      console.error('Chime error detected, closing interface:', chimeError.message);
      handleEndCall();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chimeError]);

  // Handle unexpected disconnection - close modal when session stops
  useEffect(() => {
    // Only close if we were previously connected and now we're not
    if (wasConnected && !isConnected && !isInQueue) {
      debug.log('Session disconnected unexpectedly, closing modal');
      stop();
      reset();
      const timer = setTimeout(() => {
        setWasConnected(false); // Reset for next call
        setCallStartTime(0);
        onClose();
      }, 1000); // Brief delay to show any error message
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps    
  }, [wasConnected, isConnected, isInQueue, onClose]);

  // Detect network loss - end call immediately to free up the session slot
  useEffect(() => {
    const handleOffline = () => {
      if (isConnected || isInQueue) {
        debug.log('Network connection lost, ending call');
        handleEndCall();
      }
    };

    window.addEventListener('offline', handleOffline);
    return () => window.removeEventListener('offline', handleOffline);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, isInQueue]);

  // Keyboard navigation - Escape to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        if (isInQueue) {
          handleCancelQueue();
        } else if (isConnected) {
          handleEndCall();
        } else {
          onClose();
        }
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isInQueue, isConnected]);

  // Focus management - trap focus in modal
  useEffect(() => {
    if (isOpen) {
      const modal = document.querySelector('[role="dialog"]');
      const focusableElements = modal?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      
      if (focusableElements && focusableElements.length > 0) {
        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
        
        // Focus first element when modal opens
        firstElement.focus();
        
        const handleTabKey = (e: KeyboardEvent) => {
          if (e.key === 'Tab') {
            if (e.shiftKey) {
              // Shift + Tab
              if (document.activeElement === firstElement) {
                e.preventDefault();
                lastElement.focus();
              }
            } else {
              // Tab
              if (document.activeElement === lastElement) {
                e.preventDefault();
                firstElement.focus();
              }
            }
          }
        };
        
        window.addEventListener('keydown', handleTabKey);
        return () => window.removeEventListener('keydown', handleTabKey);
      }
    }
  }, [isOpen]);

  // Initialize Chime session when connection data is available
  useEffect(() => {
    if (connectionData && sessionId && !isConnected && !isInitializing) {
      setIsInitializing(true);
      setCallStartTime(Date.now()); // Track when call starts
      reset(); // Reset timer from any previous call
      
      // Wrap in a timeout to prevent hanging on iOS Safari
      const initTimeout = setTimeout(() => {
        console.error('Call initialization timed out after 15 seconds');
        setIsInitializing(false);
        if (sessionId) {
          endSession(sessionId, 0);
        }
        onClose();
      }, 15000);
      
      initializeSession(connectionData, sessionId, queueId || undefined, feature?.name, queueJoinTime || undefined)
        .then(() => {
          clearTimeout(initTimeout);
          start();
          setIsInitializing(false);
        })
        .catch((error) => {
          clearTimeout(initTimeout);
          console.error('Failed to initialize call:', error);
          setIsInitializing(false);
          // Clean up the session from DynamoDB since initialization failed
          if (sessionId) {
            endSession(sessionId, 0);
          }
          onClose();
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionData, sessionId]);

  // Auto-end call at max duration
  useEffect(() => {
    if (elapsedTime >= maxDuration) {
      handleEndCall();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elapsedTime, maxDuration]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isConnected && sessionId && callStartTime > 0) {
        endSession(sessionId, callStartTime);
      }
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEndCall = () => {
    if (sessionId && callStartTime > 0) {
      endSession(sessionId, callStartTime);
    }
    stop();
    reset();
    setWasConnected(false); // Reset for next call
    setCallStartTime(0); // Reset for next call
    onClose();
  };

  const handleCancelQueue = async () => {
    try {
      await onCancelQueue();
      setWasConnected(false); // Reset for next call
      onClose();
    } catch (error) {
      console.error('Failed to leave queue:', error);
      // Close anyway
      setWasConnected(false); // Reset for next call
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div 
      className={styles.overlay} 
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="voice-interface-title"
      aria-describedby="voice-interface-description"
    >
      <div 
        className={styles.modal} 
        onClick={(e) => e.stopPropagation()}
        role="document"
      >
        {/* Hidden audio element for Chime SDK */}
        <audio 
          id="audio-output" 
          autoPlay 
          playsInline
          style={{ display: 'none' }} 
          aria-hidden="true" 
        />

        {isInQueue ? (
          // Queue View
          <QueueDisplay
            position={queuePosition}
            queueLength={queueLength}
            estimatedWaitTime={estimatedWaitTime}
            isConnected={isQueueConnected}
            onCancel={handleCancelQueue}
          />
        ) : isInitializing ? (
          // Loading View
          <LoadingOverlay message="Initializing call..." />
        ) : (
          // Call View
          <>
            <div className={styles.header}>
              <h2 id="voice-interface-title" className={styles.title}>
                {feature ? `Talking about ${feature.name}` : 'Voice Call'}
              </h2>
              <div 
                id="voice-interface-description"
                className={`${styles.status} ${isConnected ? styles.connected : styles.connecting}`}
                role="status"
                aria-live="polite"
                aria-atomic="true"
              >
                {isConnected ? '● Connected' : '○ Connecting...'}
              </div>
            </div>

            <Waveform />

            <CallTimer formattedTime={formattedTime} isWarning={isWarning} />

            <CallControls
              isMuted={isMuted}
              onToggleMute={toggleMute}
              onEndCall={handleEndCall}
            />

            {isWarning && (
              <div 
                className={styles.warningMessage}
                role="alert"
                aria-live="assertive"
              >
                Call will end soon due to time limit
              </div>
            )}

            {chimeError && (
              <div 
                className={styles.errorMessage}
                role="alert"
                aria-live="assertive"
              >
                {chimeError.message}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
